import { useCallback, useEffect, useRef, useState } from 'react';

import { isSsr } from '@change-corgi/core/ssr';

import { googleMapsApiClientKey } from '#config/google';

export type UseLoadGooglePlacesReturn = {
	status: 'loading' | 'loaded' | 'error';
	autocompleteService?: typeof google.maps.places.AutocompleteSuggestion;
	sessionToken?: google.maps.places.AutocompleteSessionToken;
	resetSessionToken: () => void;
};

let optionsSet = false;

async function loadGooglePlacesLibrary(): Promise<google.maps.PlacesLibrary> {
	if (isSsr()) {
		return Promise.reject(new Error('Google Places API can only be loaded on the client-side!'));
	}
	// Dynamic import avoids the top-level window.trustedTypes access in @googlemaps/js-api-loader v2.1.0+
	const { importLibrary, setOptions } = await import(
		/* webpackChunkName: "cmp/google_maps_js_api_loader" */ '@googlemaps/js-api-loader'
	);
	if (!optionsSet) {
		setOptions({
			key: googleMapsApiClientKey,
			v: 'weekly',
		});
		optionsSet = true;
	}
	return importLibrary('places');
}

export function useLoadGooglePlaces(): UseLoadGooglePlacesReturn {
	const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
	const [autocompleteService, setAutocompleteService] = useState<typeof google.maps.places.AutocompleteSuggestion>();
	const [sessionToken, setToken] = useState<google.maps.places.AutocompleteSessionToken>();
	const placesLibraryRef = useRef<google.maps.PlacesLibrary>(undefined);

	const resetSessionToken = useCallback(() => {
		if (placesLibraryRef.current) {
			setToken(new placesLibraryRef.current.AutocompleteSessionToken());
		}
	}, []);

	useEffect(() => {
		(async () => {
			try {
				const placesLibrary = await loadGooglePlacesLibrary();
				placesLibraryRef.current = placesLibrary;
				setAutocompleteService(() => placesLibrary.AutocompleteSuggestion);
				setToken(new placesLibrary.AutocompleteSessionToken());
				setStatus('loaded');
			} catch (e) {
				setStatus('error');
			}
		})();
	}, []);

	return {
		status,
		autocompleteService,
		sessionToken,
		resetSessionToken,
	};
}
